/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from 'src/components/ts-components/Text'
import { ReactNode } from 'react'
import { Tooltip } from 'antd'

interface RoleSelectionCardProps {
  onClick?: () => void
  disabled?: boolean
  title: string
  subtitle: string
  icon: ReactNode
  selected?: boolean
  /**
   * Lays the icon beside the title instead of above it, for screens where this
   * card sits among other content rather than being the whole screen. Type and
   * icon keep their full size — the row is what makes the card short, so
   * nothing has to shrink to fit.
   */
  compact?: boolean
  /** Why the card is disabled, shown as its tooltip. Defaults to the
   * out-of-spots reason, which was the only one this card had. */
  disabledReason?: string
}

const DEFAULT_DISABLED_REASON = 'Registration is Full'

const RoleSelectionCard = ({
  onClick,
  disabled = false,
  compact = false,
  title,
  subtitle,
  icon,
  selected = false,
  disabledReason = DEFAULT_DISABLED_REASON,
}: RoleSelectionCardProps) => {
  return (
    <Tooltip title={disabled ? disabledReason : ''}>
      <div
        css={css`
          /* Unselected matches DivisionCard and TeamCard exactly — a
             transparent frame with a NEUTRAL_500 hairline — so the three card
             types on this step read as one family. Selected keeps the solid
             PRIMARY fill: this is the step's loudest control, and the tab group
             below it echoes the same fill. */
          background-color: ${selected ? colors.PRIMARY : 'transparent'};
          border: ${selected ? 'none' : `1px solid ${colors.NEUTRAL_500}`};
          border-radius: 16px;
          /* Compact height is padding around the 70px icon well, so the padding
             is what sets the card's height — no fixed height the text could
             later outgrow. Both variants sit at 32px. */
          padding: 32px;
          display: flex;
          /* Compact lays the icon beside the text instead of above it, which
             lets the type stay full size while the card gets much shorter. */
          flex-direction: ${compact ? 'row' : 'column'};
          align-items: center;
          /* Compact sets the icon beside the text, so this gap is what
             separates them; the stacked variant uses the icon well's own
             margin-bottom instead. */
          gap: ${compact ? '24px' : '0'};
          text-align: ${compact ? 'left' : 'center'};
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          opacity: ${disabled ? 0.5 : 1};
          transition: border-color 0.15s ease-in-out, opacity 0.15s ease-in-out;
          min-width: ${compact ? '220px' : '300px'};
          width: 100%;
          /* The same hover the division and team cards use — the border picks
             up PRIMARY. The old lift-and-shadow moved this card in a way
             neither of the others does, which is what made it read as a
             different kind of object.

             Emitted only when there is a border to recolour: a selected card
             has none, and a disabled one is not offering to be picked. */
          ${!disabled &&
          !selected &&
          `
            &:hover {
              border-color: ${colors.PRIMARY};
            }
          `}
          @media (max-width: 768px) {
            min-width: 100%;
          }
        `}
        onClick={disabled ? undefined : onClick}
      >
        <div
          css={css`
            /* NEUTRAL_700 rather than NEUTRAL_800: the card is transparent when
               unselected, so an NEUTRAL_800 well would disappear into the page
               behind it. This reads on both the transparent frame and the
               amber fill. */
            background-color: ${colors.NEUTRAL_700};
            border-radius: 50%;
            width: 70px;
            height: 70px;
            flex: none;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: ${compact ? '0' : '24px'};
          `}
        >
          {icon}
        </div>

        {/* Its own column so the title and subtitle stack beside the icon
            rather than becoming two more items in the card's row. */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: ${compact ? 'flex-start' : 'center'};
            min-width: 0;
          `}
        >
          {/* Original type at both sizes — the row layout is what buys the
              height back, so the text does not have to shrink. */}
          <Text
            variant='heading2'
            color={selected ? PRIMARY_BUTTON_CONTENT.text : colors.NEUTRAL_0}
            style={`
              text-align: ${compact ? 'left' : 'center'};
              margin-bottom: 8px;
              word-break: break-word;
            `}
          >
            {title}
          </Text>

          <Text
            variant='paragraphMedium'
            color={selected ? PRIMARY_BUTTON_CONTENT.text : colors.NEUTRAL_50}
            style={`
              text-align: ${compact ? 'left' : 'center'};
            `}
          >
            {subtitle}
          </Text>
        </div>
      </div>
    </Tooltip>
  )
}

export default RoleSelectionCard
